import React, { useState } from "react";
import "./DatePagination.scss";
import { DateRange, WeekInfo } from "../../Types";
import { findWeekIndexFromRange } from "../utils";
import { Button } from "@evidenceb/athena-common/design-system/Button";
interface Props {
    onWeekChange: (week: DateRange) => void;
    weeksInfo: WeekInfo[];
}
const DatePagination = ({ onWeekChange, weeksInfo }: Props) => {
    const [curentDate, setCurrentDate] = useState<WeekInfo>(weeksInfo[0]);
    const dateChange = (action: "next" | "previous") => {
        let weekIndex = findWeekIndexFromRange(curentDate.range, weeksInfo);
        // we don't want -1 index, use 0 (current week) instead
        weekIndex = weekIndex === -1 ? 0 : weekIndex;
        const nextIndex = action === "next" ? weekIndex - 1 : weekIndex + 1;

        if (weeksInfo[nextIndex]) {
            onWeekChange(weeksInfo[nextIndex].range);
            setCurrentDate(weeksInfo[nextIndex]);
        }
    };

    return (
        <div className="month-pagination">
            <Button
                variant="primary"
                disabled={isDisabled("previous", curentDate, weeksInfo)}
                onClick={() => dateChange("previous")}
                icons={[
                    {
                        path: "chevron_left",
                    },
                ]}
                hideLabel={true}
                label="Previous"
            ></Button>
            <p>{formatDateRangeToString(curentDate.range)}</p>
            <Button
                variant="primary"
                disabled={isDisabled("next", curentDate, weeksInfo)}
                onClick={() => dateChange("next")}
                icons={[
                    {
                        path: "chevron_right",
                    },
                ]}
                hideLabel={true}
                label="Next"
            ></Button>
        </div>
    );
};

export default DatePagination;

const isDisabled = (
    action: "next" | "previous",
    curentWeek: WeekInfo,
    weeksInfo: WeekInfo[]
) => {
    if (curentWeek.index === weeksInfo.length - 1 && action === "previous")
        return true;
    if (curentWeek.index === 0 && action === "next") return true;
    return false;
};

// Helper function to get the ordinal suffix for a day in English
function getOrdinalSuffix(day: number): string {
    if (day > 3 && day < 21) return "th"; // Special case for 11th, 12th, 13th, etc.
    switch (day % 10) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
}

// Function to format a date based on locale (Month-Day-Year or Day-Month-Year)
function formatDateToLocale(date: Date): string {
    const day = date.getDate();
    const preferredLanguages = navigator.languages || [navigator.language];
    const month = new Intl.DateTimeFormat(preferredLanguages[0], {
        month: "short",
    }).format(date);

    // If the locale is English, use MDY format with ordinal suffix for the day
    if (preferredLanguages[0].startsWith("en")) {
        const ordinalSuffix = getOrdinalSuffix(day);
        return `${month} ${day}${ordinalSuffix}`;
    } else {
        // For most other languages (DMY format)
        return `${day} ${month}`;
    }
}

// Main function to format the date range based on the locale
function formatDateRangeToString(dateRange: DateRange): string {
    const { start, end } = dateRange;
    const startFormatted = formatDateToLocale(start);
    const endFormatted = formatDateToLocale(end);

    // If the start and end dates are in the same year, show the year only once
    const year = start.getFullYear();
    return `${startFormatted} - ${endFormatted}, ${year}`;
}
